/*
 * Shared, FORCED detail-tab building blocks — reused by BOTH the gateway panel and the
 * server panel so every tab has the identical shape: a row of exactly 3 radial dials on
 * top, then a 4-field grid below. The dial row never changes height between tabs (no
 * layout shift); a metric that isn't wired yet renders the dial's pending "—" and the
 * field's DASH placeholder, so the structure is in place and wiring later is a one-line
 * populate.
 */
import { html, type TemplateResult } from 'lit';
import { ring, type RingOpts } from './ring';
import { DASH } from '../core/fleet-model';

/** The 3-dial row pinned at the top of every detail tab. Pass exactly 3 RingOpts; a dial
 *  with `value: null` shows the pending "—" (metric not wired yet). Size/stroke are fixed
 *  here so all dials everywhere match. */
export function dialRow(dials: RingOpts[]): TemplateResult {
  return html`<div class="fgp-rings">${dials.map((d) => ring({ size: 50, stroke: 4, ...d }))}</div>`;
}

export interface Field {
  label: string;
  value: string;
  mono?: boolean;
}

/** The 4-field grid below the dials. An empty value renders the DASH placeholder (a fact
 *  not wired yet) in the muted style — so a not-yet-available field reads as pending, not
 *  broken. */
export function fieldGrid(fields: Field[]): TemplateResult {
  return html`<div class="fgp-kv-grid">
    ${fields.map((f) => {
      const placeholder = !f.value || f.value === DASH;
      return html`<div class="fgp-kv">
        <div class="fgp-kv-label">${f.label}</div>
        <div class="fgp-kv-value ${f.mono ? 'is-mono' : ''} ${placeholder ? 'is-placeholder' : ''}">
          ${placeholder ? DASH : f.value}
        </div>
      </div>`;
    })}
  </div>`;
}

/** A whole tab body = the dial row + the field grid. The one shape every detail tab uses. */
export function tabBody(dials: RingOpts[], fields: Field[]): TemplateResult {
  return html`${dialRow(dials)}${fieldGrid(fields)}`;
}

// ── shared metric formatters (consistent across both panels) ──

/** GiB → a compact capacity string ("64 GB" / "7.7 TB"). DASH when 0/unknown. */
export function fmtGb(gb: number): string {
  if (!gb || gb < 0) return DASH;
  if (gb >= 1024) return `${(gb / 1024).toFixed(gb >= 10240 ? 0 : 1)} TB`;
  return `${Math.round(gb)} GB`;
}

/** A count → string, DASH when 0 (unavailable). */
export function fmtCount(n: number): string {
  return n > 0 ? String(n) : DASH;
}

/** A large count → compact string (13_971_392 → "13.97M", 12_400 → "12.4k"). For
 *  data-point / row totals where the raw integer is unreadable. DASH when 0. */
export function fmtCompact(n: number): string {
  if (!n || n < 0) return DASH;
  if (n >= 1e9) return `${(n / 1e9).toFixed(2)}B`;
  if (n >= 1e6) return `${(n / 1e6).toFixed(2)}M`;
  if (n >= 1e4) return `${(n / 1e3).toFixed(1)}k`;
  return String(Math.round(n));
}

/** Load average as a % of the core count (capped 100). null when either is missing
 *  → the dial renders pending. */
export function loadPct(load1: number, cores: number): number | null {
  return cores > 0 && load1 >= 0 ? Math.min(100, Math.round((load1 / cores) * 100)) : null;
}

